import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { useAuth } from "../../../../firebase/AuthProvider";
import NewTemplateTaskAction from "../../../../interfaces/templateTask/NewTemplateTaskAction";
import ActionType from "../../../../interfaces/task/taskActions/ActionType";
import ActionSubType from "../../../../interfaces/task/taskActions/ActionSubType";
import {
  getTaskActionSubTypes,
  getTaskActionTypes,
} from "../../../../services/taskServices/TaskActionService";
import Banner from "../../../ui/Banner/Banner";
import { publishTemplateTaskAction } from "../../../../services/templateService/TemplateTaskActionService";
import { Button } from "react-bootstrap";
import { unset } from "lodash";

interface AddTaskActionProps {
  taskId: number | undefined;
  reload: () => void;
}

interface OptionType {
  value: string;
  label: string;
}

const AddTaskAction: React.FC<AddTaskActionProps> = ({
  taskId,
  reload,
}) => {
  const { currentUser } = useAuth();

  const [actionTypeOptions, setActionTypeOptions] = useState<OptionType[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [actionSubTypeOptions, setActionSubTypeOptions] = useState<
    OptionType[]
  >([]);

  const [actionTypes, setActionTypes] = useState<ActionType[] | null>();
  const [actionSubTypes, setActionSubTypes] = useState<
    ActionSubType[] | null
  >();
  const [newTask, setNewTask] = useState<NewTemplateTaskAction>({
    taskId: "",
    description: "",
    actionTypeId: "",
    actionSubTypeId: "",
    estimated_ttc: "",
  });

  useEffect(() => {
    const fetchLkValues = async () => {
      if (currentUser !== null) {
        const authToken: string = await currentUser.getIdToken();
        const fetchedActionTypes: ActionType[] | null =
          await getTaskActionTypes(authToken);
        const fetchedActionSubTypes: ActionSubType[] | null =
          await getTaskActionSubTypes(authToken);
        setActionTypes(fetchedActionTypes);
        setActionSubTypes(fetchedActionSubTypes);
      }
    };

    if (actionTypes && actionSubTypes) {
      const formattedActionTypes = actionTypes.map((actionType) => ({
        value: actionType.id.toString(),
        label: actionType.actionTypeName,
      }));
      const formattedActionSubTypes = actionSubTypes.map((actionSubType) => ({
        value: actionSubType.id.toString(),
        label: actionSubType.actionSubTypeName,
      }));

      setActionTypeOptions(formattedActionTypes);
      setActionSubTypeOptions(formattedActionSubTypes);
    } else {
      fetchLkValues();
    }
  }, [actionTypes, actionSubTypes]);

  const handleActionTypeChange = (selectedOption: SingleValue<OptionType>) => {
    const actionTypeId = selectedOption ? selectedOption.value : "";
    setNewTask({ ...newTask, actionTypeId });
  };

  const handleActionSubTypeChange = (
    selectedOption: SingleValue<OptionType>
  ) => {
    const actionSubTypeId = selectedOption ? selectedOption.value : "";
    setNewTask({ ...newTask, actionSubTypeId });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setNewTask({ ...newTask, [name]: value });
  };

  const addTemplateTaskAction = () => {
    // Verify that NewTemplateTaskAction filled out to completion
    if (
      newTask.description !== "" &&
      newTask.actionTypeId !== "" &&
      newTask.actionSubTypeId !== ""
    ) {
      submitTemplateTaskActions();
      setNewTask({
        taskId: "",
        description: "",
        actionTypeId: newTask.actionTypeId,
        actionSubTypeId: newTask.actionSubTypeId,
        estimated_ttc: "",
      });
    } else {
      console.log("setting err message");
      setWarningMessage(
        "Description, Action Type, and Action Sub Type are all required fields!"
      );
    }
  };

  const dismissErrorBanner = () => {
    setErrorMessage("");
  };
  const dismissWarningBanner = () => {
    setWarningMessage("");
  };

  const submitTemplateTaskActions = async () => {
    if (taskId !== undefined && currentUser !== null) {
      try {
        const authToken: string = await currentUser.getIdToken();
        // Create an array of promises
        publishTemplateTaskAction(newTask, taskId, authToken)
          .then((data: number | null) => {
            reload();
          })

        // Wait for all promises to resolve
        // Only executed if all promises resolve successfully
      } catch (error) {
        setErrorMessage(
          "Unable to add required task actions to this template task, consider contact IT if this issue persists."
        );
        console.error("An error occurred:", error);
        // Handle any errors here (e.g., show an error message to the user)
      }
    }
  };

  return (
    <>
      <div className="common_card h-100">
        <div className="">
          {errorMessage !== "" && (
            <Banner
              message={errorMessage}
              dismissBanner={dismissErrorBanner}
              status="error"
            />
          )}
        </div>
        <div className="">
          {warningMessage !== "" && (
            <Banner
              message={errorMessage}
              dismissBanner={dismissWarningBanner}
              status="warning"
            />
          )}
        </div>
        <div className="top border_tittle mby_16">
          <div className="top_bar_tittle">
            <h2> Feeling a little, "Schedule-y" today?{" "}</h2>
            <p>Add some sub-tasks to help spice up the daily grind!</p>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="custom_input_flled">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                placeholder="A brief description of what the action entails..."
                value={newTask.description}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="custom_input_flled">
              <label htmlFor="actionType">Task Type:</label>
              <Select
                id="actionType"
                name="actionType"
                value={actionTypeOptions.find(
                  (option) => option.value === newTask.actionTypeId
                )}
                onChange={handleActionTypeChange}
                options={actionTypeOptions}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="custom_input_flled">
              <label htmlFor="actionSubType">Task Sub-Type</label>
              <Select
                id="actionSubType"
                name="actionSubType"
                value={actionSubTypeOptions.find(
                  (option) => option.value === newTask.actionSubTypeId
                )}
                onChange={handleActionSubTypeChange}
                options={actionSubTypeOptions}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div>
          <div className="col-md-12">
            <div className="custom_input_flled mb-0">
              <label htmlFor="estimated_ttc">Estimated Time To Completion (TTC) In Minutes</label>
              <input
                id="estimated_ttc"
                type="number"
                placeholder="Enter a number"
                min="0"
              />
            </div>
          </div>
        </div>
        <div className="form_btn_flx mby_t_24">
          <Button variant="primary" onClick={addTemplateTaskAction}>Add</Button>
        </div>
      </div>
    </>
  );
};

export default AddTaskAction;
